<script setup lang="ts">
import SectionWelcomeHeader from '@/components/sections/welcome/section-welcome-header.vue';
import SectionWelcomeHero from '@/components/sections/welcome/section-welcome-hero.vue';
import SectionWelcomeAuth from '@/components/sections/welcome/section-welcome-auth.vue';
import SectionWelcomePromo from '@/components/sections/welcome/section-welcome-promo.vue';
import PartWelcomeHelp from '@parts/part-welcome-help.vue';
import CoreLoader from '@core/core-loader.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/store/user';
import { useAuthApi } from '@/store/api/authApi';
import { useRoute, useRouter } from 'vue-router';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useToast } from 'vue-toast-notification';
import { useSocialProviders } from '@/composables/socialProviders';
import { SocialProvider } from '@store/api/interfaces';
import { ApiError } from '@/composables/useApi';
import { usePaymentFlowStore } from '@store/paymentFlow';
import { usePaymentApi } from '@store/api/paymentApi';
import { getAutoSentRemaining, markAutoSent, clearAutoSent, getRetryAfterFromError } from '@composables/otpAutoSend';
import { handoffCode, authErrorCode } from '@composables/handoffCapture';
import { consumeHandoffCode } from '@composables/handoff';

const toast = useToast();
const userStore = useUserStore();
const authApi = useAuthApi();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const socialProviders = useSocialProviders();
const paymentFlow = usePaymentFlowStore();
const paymentApi = usePaymentApi();

const step = ref<'email' | 'otp'>('email');
const sendOtpLoading = ref(false);
const verifyOtpLoading = ref(false);
const otpError = ref(false);
const otpCode = ref('');
const canResend = ref(false);
const timerKey = ref(0);
const timerDuration = ref(60);
const authSection = ref<InstanceType<typeof SectionWelcomeAuth> | null>(null);

const loginForm = ref({
	email: '',
});

const v$ = useVuelidate({
	email: { required, email },
}, loginForm);

const getRedirectTarget = () => {
	const redirect = route.query.redirect as string;
	if (redirect === 'payment') {
		const query: Record<string, string> = {};
		if (paymentFlow.promoCode) {
			query.promo = paymentFlow.promoCode;
		}
		return { name: 'Payment', query };
	}
	return { name: 'Home' };
};

const applyPromoAfterLogin = async () => {
	if (!paymentFlow.promoCode) return;
	try {
		await paymentApi.http.applyPromoCode(paymentFlow.promoCode);
		toast.success(t('payment.promo_applied'), { position: 'top' });
	} catch (e) {
		console.error('Failed to apply promo after login', e);
		// Forget a rejected code so getRedirectTarget doesn't carry it to payment
		const code = paymentApi.methods.promoRejectionCode(e);
		if (code) {
			paymentFlow.clearPromo();
			toast.error(t(`errorCode.${code}`), { position: 'top' });
		}
	}
};

const scrollToAuth = () => {
	(authSection.value as any)?.$el?.scrollIntoView({ behavior: 'smooth' });
};

const authCallbackHandler = async (provider: SocialProvider) => {
	try {
		// A successful OAuth return carries nothing but a one-time code in the
		// fragment (`#handoff=<code>`), captured and stripped from the address
		// at startup. Its absence means the attempt failed, and the backend put
		// the reason in `?code=` (captured alongside).
		if (!handoffCode) {
			throw new ApiError({
				success: false,
				data: {},
				message: `Failed ${provider} auth`,
				code: authErrorCode ?? 'auth_by_provider_failed',
			});
		}

		const established = await consumeHandoffCode(handoffCode, provider);
		if (!established) {
			throw new ApiError({
				success: false,
				data: {},
				message: 'Handoff exchange failed',
				code: 'handoff_invalid',
			});
		}

		await applyPromoAfterLogin();
		router.push(getRedirectTarget());
	} catch (error) {
		console.error('ErrorProvideAuth', error);
		if (error instanceof ApiError) {
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
		router.push({ name: 'Welcome' });
	}
};

const onSendOtp = async () => {
	try {
		if (sendOtpLoading.value) return;

		const validate = await v$.value.$validate();
		if (!validate) return;

		sendOtpLoading.value = true;
		const retryAfter = await authApi.methods.sendOtp(loginForm.value.email);
		timerDuration.value = retryAfter || 60;
		step.value = 'otp';
		otpCode.value = '';
		otpError.value = false;
		canResend.value = false;
		timerKey.value++;
	} catch (error) {
		console.error('SendOtpError', error);
		if (error instanceof ApiError) {
			// On a resend-cooldown 429, a code was already sent — move to the
			// code step and start the exact server countdown from `retry_after`.
			const retryAfter = error.code === 'otp_rate_limited' ? getRetryAfterFromError(error) : null;
			if (retryAfter) {
				step.value = 'otp';
				timerDuration.value = retryAfter;
				canResend.value = false;
				timerKey.value++;
			}
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		sendOtpLoading.value = false;
	}
};

const onVerifyOtp = async () => {
	try {
		if (verifyOtpLoading.value) return;
		if (otpCode.value.length < 6) return;

		verifyOtpLoading.value = true;
		otpError.value = false;

		await userStore.methods.login({
			email: loginForm.value.email,
			code: otpCode.value,
			lang: locale.value,
		});
		clearAutoSent();
		await applyPromoAfterLogin();
		router.push(getRedirectTarget());
	} catch (error) {
		console.error('VerifyOtpError', error);
		otpError.value = true;
		if (error instanceof ApiError) {
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top',
				pauseOnHover: true
			});
		} else {
			toast.error(t('login.otp.error'), {
				position: 'top',
				pauseOnHover: true
			});
		}
	} finally {
		verifyOtpLoading.value = false;
	}
};

const onTimerEnd = () => {
	canResend.value = true;
};

const onResendOtp = async () => {
	try {
		if (sendOtpLoading.value) return;
		sendOtpLoading.value = true;
		const retryAfter = await authApi.methods.sendOtp(loginForm.value.email);
		timerDuration.value = retryAfter || 60;
		canResend.value = false;
		timerKey.value++;
		otpCode.value = '';
		otpError.value = false;
	} catch (error) {
		console.error('ResendOtpError', error);
		if (error instanceof ApiError) {
			// Rate-limited resend: restart the countdown with the exact server value.
			const retryAfter = error.code === 'otp_rate_limited' ? getRetryAfterFromError(error) : null;
			if (retryAfter) {
				timerDuration.value = retryAfter;
				canResend.value = false;
				timerKey.value++;
			}
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		sendOtpLoading.value = false;
	}
};

const goBackToEmail = () => {
	step.value = 'email';
	otpCode.value = '';
	otpError.value = false;
};

const onSocialAuth = (provider: string) => {
	// Save redirect target so login.vue can redirect after callback
	const redirect = route.query.redirect as string || '/';
	let redirectPath = redirect === 'payment' ? '/payment' : '/';
	if (paymentFlow.promoCode && redirect === 'payment') {
		redirectPath = `/payment?promo=${encodeURIComponent(paymentFlow.promoCode)}`;
	}
	sessionStorage.setItem('welcome-redirect', redirectPath);
	socialProviders.auth(provider as SocialProvider);
};

// Prefill email passed by the marketing landing (?email=...) and jump
// straight to the code-entry step. Invalid values are silently ignored.
const handleEmailFromQuery = async () => {
	const rawEmail = route.query.email;
	const emailFromQuery = (Array.isArray(rawEmail) ? rawEmail[0] : rawEmail)?.trim();
	if (!emailFromQuery) return;

	loginForm.value.email = emailFromQuery;
	const valid = await v$.value.$validate();
	v$.value.$reset();
	if (!valid) {
		loginForm.value.email = '';
		return;
	}

	const remaining = getAutoSentRemaining(emailFromQuery);
	if (remaining !== null) {
		// Code was already auto-sent in this tab (page reload) — restore
		// the OTP step with the remaining resend timer instead of resending
		step.value = 'otp';
		timerDuration.value = remaining;
		canResend.value = remaining <= 0;
	} else {
		await onSendOtp();
		if (step.value === 'otp') {
			markAutoSent(emailFromQuery, timerDuration.value);
		}
	}
};

onMounted(async () => {
	// Save promo code from URL to store
	const promoFromQuery = route.query.promo as string | undefined;
	if (promoFromQuery) {
		paymentFlow.promoCode = promoFromQuery;
	}

	const { callback } = route.params;
	if (!!callback && typeof callback === 'string') {
		await authCallbackHandler(callback as SocialProvider);
		return;
	}

	await handleEmailFromQuery();
});
</script>

<template>
	<main class="page-welcome" v-if="!route.params?.callback">
		<section-welcome-header @scroll-to-auth="scrollToAuth" />

		<div class="page-welcome__content">
			<section-welcome-hero />

			<section-welcome-auth
				ref="authSection"
				:step="step"
				:login-form="loginForm"
				:send-otp-loading="sendOtpLoading"
				:verify-otp-loading="verifyOtpLoading"
				:otp-code="otpCode"
				:otp-error="otpError"
				:can-resend="canResend"
				:timer-key="timerKey"
				:timer-duration="timerDuration"
				:validation="v$"
				@send-otp="onSendOtp"
				@verify-otp="onVerifyOtp"
				@resend-otp="onResendOtp"
				@go-back="goBackToEmail"
				@update:email="loginForm.email = $event"
				@update:otp-code="otpCode = $event"
				@social-auth="onSocialAuth"
				@timer-end="onTimerEnd"
			/>
		</div>

		<section-welcome-promo />

		<part-welcome-help />
	</main>
	<core-loader v-else absolute />
</template>

<style lang="scss">
.page-welcome {
	min-height: 100dvh;
	width: 100%;
	padding-bottom: 8rem;
	background: #fff;

	@include theme-dark {
		background: linear-gradient(180deg, #000000 0%, #735DCF 178.19%);
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 3.2rem;
		padding: 0 1.5rem;
		max-width: 68rem;
		margin: 0 auto;
	}
}
</style>
